import { useCallback, useState } from 'react';

import useEditableStateOfType from 'common/hooks/useEditableStateOfType';
import useErrorHandledMutation, {
  throwOnlyUnhandledErrors,
} from 'common/hooks/useErrorHandledMutation';

import {
  CheckCanUserCanProceedFn,
  PersonalStatusHandlersHook,
  UpdatePersonalStatusInput,
} from './types';
import { UpdateUserProfileDocument } from './updateUserProfile.gql';

const checkIfUserCanProceed: CheckCanUserCanProceedFn = ({
  hasForeignFiscalResidence,
  isSpecifiedUSPerson,
}) => {
  return !isSpecifiedUSPerson && !hasForeignFiscalResidence;
};

const usePersonalStatusHandlers: PersonalStatusHandlersHook = ({
  destinationPageOnRejection,
  destinationPageOnSuccess,
  setCurrentPage,
  user,
}) => {
  const [showUSSpecifiedPersonError, setShowUSSpecifiedPersonError] =
    useState(false);
  const [showForeignFiscalResidenceError, setShowForeignFiscalResidenceError] =
    useState(false);
  const { editedObject: editedUser, updateProperty } = useEditableStateOfType(
    user,
    ['isSpecifiedUSPerson', 'hasForeignFiscalResidence'],
  );

  const [saveProfile, { loading }] = useErrorHandledMutation<
    unknown,
    UpdatePersonalStatusInput
  >(UpdateUserProfileDocument, {
    variables: {
      profile: {
        hasForeignFiscalResidence: editedUser.hasForeignFiscalResidence,
        isSpecifiedUSPerson: editedUser.isSpecifiedUSPerson,
      },
    },
  });
  const save = useCallback(() => {
    saveProfile()
      .catch(throwOnlyUnhandledErrors)
      .then(() => {
        const canUserProceed = checkIfUserCanProceed(editedUser);

        if (canUserProceed) {
          setCurrentPage(destinationPageOnSuccess);
        } else {
          setCurrentPage(destinationPageOnRejection);
        }
      });
  }, [
    setCurrentPage,
    saveProfile,
    editedUser,
    destinationPageOnSuccess,
    destinationPageOnRejection,
  ]);

  const handleFormSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>): void => {
      event.preventDefault();

      let hasMissingField = false;

      if (editedUser.isSpecifiedUSPerson === null) {
        hasMissingField = true;
        setShowUSSpecifiedPersonError(true);
      }

      if (editedUser.hasForeignFiscalResidence === null) {
        hasMissingField = true;
        setShowForeignFiscalResidenceError(true);
      }

      if (!hasMissingField) {
        save();
      }
    },
    [
      editedUser.hasForeignFiscalResidence,
      editedUser.isSpecifiedUSPerson,
      save,
    ],
  );

  const updateIsSpecifiedUSPerson = useCallback(
    (value: boolean) => {
      updateProperty('isSpecifiedUSPerson', value);
      // Always mark that the error is no longer needed
      setShowUSSpecifiedPersonError(false);
    },
    [updateProperty],
  );
  const updateHasForeignFiscalResidence = useCallback(
    (value: boolean) => {
      updateProperty('hasForeignFiscalResidence', value);
      // Always mark that the error is no longer needed
      setShowForeignFiscalResidenceError(false);
    },
    [updateProperty],
  );

  return {
    handleFormSubmit,
    hasForeignFiscalResidence: editedUser.hasForeignFiscalResidence,
    isSpecifiedUSPerson: editedUser.isSpecifiedUSPerson,
    loading,
    showForeignFiscalResidenceError,
    showUSSpecifiedPersonError,
    updateHasForeignFiscalResidence,
    updateIsSpecifiedUSPerson,
  };
};

export default usePersonalStatusHandlers;
